"use client";

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "next/navigation";

const CLIENT_URL_CHANGE_EVENT = "client-url-change";

/**
 * Keeps filter-only URL changes in sync without asking Next.js to navigate and
 * re-render the server component tree.
 */
export function useClientSearchParams() {
  const nextSearchParams = useSearchParams();
  const nextSearch = nextSearchParams.toString();
  const [search, setSearch] = useState(nextSearch);

  useEffect(() => {
    setSearch(nextSearch);
  }, [nextSearch]);

  useEffect(() => {
    const syncSearch = () => setSearch(window.location.search);

    window.addEventListener("popstate", syncSearch);
    window.addEventListener(CLIENT_URL_CHANGE_EVENT, syncSearch);

    return () => {
      window.removeEventListener("popstate", syncSearch);
      window.removeEventListener(CLIENT_URL_CHANGE_EVENT, syncSearch);
    };
  }, []);

  return useMemo(() => new URLSearchParams(search), [search]);
}

export function replaceClientSearchParams(
  pathname: string,
  searchParams: URLSearchParams,
) {
  const url = searchParams.toString()
    ? `${pathname}?${searchParams.toString()}`
    : pathname;

  window.history.replaceState(window.history.state, "", url);
  window.dispatchEvent(new Event(CLIENT_URL_CHANGE_EVENT));
}
